import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle, Package, Mail, ArrowRight, Truck } from "lucide-react";
import { http } from "../../lib/api";

const STATUS_COLORS = {
  pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
  paid: "bg-blue-50 text-blue-700 border-blue-200",
  processing: "bg-purple-50 text-purple-700 border-purple-200",
  shipped: "bg-indigo-50 text-indigo-700 border-indigo-200",
  delivered: "bg-green-50 text-green-700 border-green-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

export default function OrderConfirmation() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get(`/store/orders/${id}`)
      .then(r => setOrder(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="max-w-xl mx-auto animate-pulse space-y-4 py-12">
      <div className="h-8 rounded bg-gray-100 w-1/2 mx-auto" />
      <div className="h-32 rounded-2xl bg-gray-100" />
    </div>
  );

  if (!order) return (
    <div className="text-center py-20">
      <div className="text-lg font-semibold text-gray-500">Order not found</div>
      <Link to="/store/orders" className="mt-4 inline-block text-sm font-semibold text-yellow-600">View all orders →</Link>
    </div>
  );

  const statusClass = STATUS_COLORS[order.status] || STATUS_COLORS.pending;

  return (
    <div className="max-w-2xl mx-auto space-y-8 py-6">
      {/* Success header */}
      <div className="text-center space-y-3">
        <div className="w-20 h-20 rounded-full bg-green-50 border-4 border-green-100 grid place-items-center mx-auto">
          <CheckCircle size={36} className="text-green-500" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Cormorant Garamond, serif" }}>Order Confirmed!</h1>
        <p className="text-gray-600 text-sm">Thank you for your purchase. We'll send updates to <strong>{order.shipping_address?.email || "your email"}</strong>.</p>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${statusClass}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-current" />{order.status?.toUpperCase()}
        </span>
      </div>

      {/* Order details card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-0.5">Order Number</div>
            <div className="font-bold text-gray-900">#{order.order_number || order.id?.slice(-8).toUpperCase()}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-400 mb-0.5">{new Date(order.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</div>
            <div className="font-bold text-gray-900">${Number(order.total).toFixed(2)}</div>
          </div>
        </div>

        {/* Items */}
        <div className="p-5 space-y-3">
          {(order.items || []).map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-50 shrink-0">
                {item.product_image && <img src={item.product_image} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-gray-900 truncate">{item.name}</div>
                {item.variant_label && <div className="text-xs text-gray-500">{item.variant_label}</div>}
                <div className="text-xs text-gray-400">Qty: {item.qty}</div>
              </div>
              <div className="font-bold text-sm text-gray-900">${(item.price * item.qty).toFixed(2)}</div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-1 text-sm">
          <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>${Number(order.subtotal || 0).toFixed(2)}</span></div>
          {order.discount_amount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span>-${Number(order.discount_amount).toFixed(2)}</span></div>}
          <div className="flex justify-between text-gray-600"><span>Shipping</span><span>{order.shipping_cost === 0 ? "Free" : `$${Number(order.shipping_cost || 0).toFixed(2)}`}</span></div>
          <div className="flex justify-between font-bold text-gray-900 pt-1 border-t border-gray-100"><span>Total</span><span>${Number(order.total).toFixed(2)}</span></div>
        </div>
      </div>

      {/* Shipping address */}
      {order.shipping_address && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-2 font-bold text-gray-900 mb-3"><Truck size={16} className="text-yellow-600" /> Shipping To</div>
          <div className="text-sm text-gray-600 space-y-0.5">
            <div className="font-semibold text-gray-900">{order.shipping_address.name}</div>
            <div>{order.shipping_address.line1}{order.shipping_address.line2 ? `, ${order.shipping_address.line2}` : ""}</div>
            <div>{[order.shipping_address.city, order.shipping_address.state, order.shipping_address.zip].filter(Boolean).join(", ")}</div>
            <div>{order.shipping_address.country}</div>
          </div>
        </div>
      )}

      {/* Notifications note */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-2xl p-4 text-sm text-blue-700">
        <Mail size={16} className="text-blue-500 shrink-0 mt-0.5" />
        <span>You'll receive email updates for payment confirmation, shipping, and delivery. Check your inbox (and spam folder).</span>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Link to="/store/orders" className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-bold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors">
          <Package size={16} /> View All Orders
        </Link>
        <Link to="/store" className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-white" style={{ background: "#0F1E38" }}>
          Continue Shopping <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}
